import { hasMapEntitlement, MAP_FEATURE_ENTITLEMENTS, } from './entitlements.js';
/**
 * Krok P1MAP4 — NOSIC UROVNE VEREJNE MAPY (zadani bod 1, GO R.B. 19. 10. 2026).
 *
 * Do tohoto kroku mela verejna mapa uroven zapsanou v kodu stranky
 * (`routes/mapa.tsx`: `const mapLevel: MapEntitlementLevel = 'Z'`), takze
 * hledac, detail rizik, teren i site byly zamcene VSEM — i tomu, kdo si
 * report zaplatil. Tabulka urovni (`MAP_FEATURE_ENTITLEMENTS`) byla hotova,
 * chybel jen nosic: ODKUD se uroven bere.
 *
 * ODKUD SE BERE A KAM TECE (pravidlo zadani):
 *   * `hasActiveSubscription` <- `commerce_entitlements` (narok ACCESS
 *     „Pristup na 30 dni" nebo MEMBERSHIP z tarifu kancelare, ktery je
 *     v den dotazu pouzitelny) -> `baseLevel` pro VSECHNY parcely,
 *   * `reportPoints` <- potvrzeny bod (`commerce_orders.latitude/longitude`)
 *     ZAPLACENE objednavky reportu -> uroven `R` pro parcelu v okoli toho
 *     bodu, natrvalo (narok nema platnost — koupeny report nezanika),
 *   * nic z toho = `Z` (dnesni stav pro anonyma).
 *
 * TOHLE JE JEDINE MISTO, KDE SE UROVEN SKLADA (zadani bod 2: „jeden zdroj
 * pravdy pro prava, zadne druhe tabulky"). Cte ho API (`/v1/public/map-access`
 * v commerce), geo-api (overeni podepsaneho nosice, `@reos/map/grant`)
 * i verejny web (`mapLevel` v panelu). Zadna z tech tri stran nema vlastni
 * tabulku ani vlastni pravidlo.
 */
/** Uroven, kterou dava aktivni predplatne (tarif kancelare / Pristup na 30 dni). */
export const MAP_SUBSCRIPTION_LEVEL = 'T';
/** Uroven, kterou dava koupeny report — pro JEDNU nemovitost, ne pro vsechny. */
export const MAP_REPORT_LEVEL = 'R';
/**
 * „A jeji okoli v rozsahu reportu" (zadani bod 1) v METRECH od potvrzeneho
 * bodu objednavky.
 *
 * Proc vzdalenost a ne seznam parcelnich cisel: objednavka nese POTVRZENY
 * BOD (`commerce_orders.latitude/longitude`; parcelni cil bez souradnic
 * objednat nejde, viz `resolveTarget` v `order-service.ts`), ne id parcely
 * z RUIAN — ta zije jen v geo-db. Bod umi obe strany porovnat bez toho, aby
 * si commerce a geo-api musely vymenovat identifikatory parcel.
 *
 * 500 m je tataz hodnota, jakou geo-api uz pouziva pro „okoli parcely"
 * (`CONTEXT_SEARCH_RADIUS_M` v `apps/geo-api/src/db.ts`) — rozsah, ze
 * ktereho report bere kontext. Hodnota jde V PODEPSANEM NOSICI, takze jeji
 * zmena nepotrebuje nasazeni geo-api.
 */
export const MAP_REPORT_UNLOCK_RADIUS_M = 500;
/**
 * Krok P1MAP2b bod 4 — UKAZKOVE PARCELY (volna ukazka jako u Pozemkova).
 *
 * Pozemkov placene udaje (druh vlastnictvi, vecna bremena, cenove udaje,
 * nesoulady) pusti zdarma na SEDMI ukazkovych parcelach — kdo si chce
 * prohlednout, co se prodava, nemusi platit. Tohle je tatazna ukazka:
 * na parcelach v seznamu nize vydava `B5`–`B8` i anonymovi.
 *
 * ID jsou RUIAN `parcely.id` (ne parcelni cisla) z DEMO vyrezu, nad kterym
 * jde cely produkt ukazovat — k.u. 774294 Umyslovice (zlata parcela 38/13
 * a jeji sousedi, stejny vyrez jako zlate testy MAP1) a k.u. 727181 Nove
 * Mesto nad Metuji. Seznam je ZAMERNE v kodu, ne v DB: ukazka je soucast
 * nabidky, ne dat tenanta, a `@reos/map` je jedine misto, kde se sklada
 * uroven (zadani P1MAP4 bod 2 — jeden zdroj prav).
 *
 * POZOR: ukazka plati JEN na `B5`–`B8` (zadani bod 4), ne na celou mapu.
 * Proto ji neuplatnuje `mapLevelAt`/`mapLevelForParcel`, ale az routa
 * tematiky — jinak by ukazkova parcela zdarma odemkla i souhrn rizik
 * (`B14`), hledac nebo detail terenu.
 */
export const MAP_SAMPLE_PARCEL_IDS = [
    /* 774294 Umyslovice 38/13 — zlata parcela (1 731 m², zahrada). */
    '2026820208',
    /* 774294 Umyslovice 38 — kmen bez poddeleni. */
    '2026642208',
    /* 774294 Umyslovice 38/2. */
    '2026812208',
    /* 774294 Umyslovice 470/1 — velky pozemek (10 986 m²). */
    '89602369010',
    /* 727181 Nove Mesto nad Metuji 38. */
    '2069613101',
];
const MAP_SAMPLE_PARCEL_ID_SET = new Set(MAP_SAMPLE_PARCEL_IDS);
/** `true`, kdyz je parcela v ukazce zdarma (`MAP_SAMPLE_PARCEL_IDS`). */
export function isMapSampleParcel(parcelId) {
    return MAP_SAMPLE_PARCEL_ID_SET.has(parcelId);
}
/**
 * Uroven pro parcelu se zapocitanou VOLNOU UKAZKOU. Na ukazkove parcele
 * plati aspon `MAP_REPORT_LEVEL` (= uroven koupeneho reportu), jinak se
 * uroven nemeni. Volajici: routa tematiky v geo-api a jeji blok v panelu
 * verejneho webu — obe strany TOUTO funkci, aby pravidlo nebylo ve dvou
 * kopiich.
 */
export function mapLevelForSampleParcel(level, parcelId) {
    if (!isMapSampleParcel(parcelId))
        return level;
    return hasMapEntitlement(level, MAP_REPORT_LEVEL) ? level : MAP_REPORT_LEVEL;
}
export const ANONYMOUS_MAP_ACCESS = {
    baseLevel: 'Z',
    reportPoints: [],
    source: 'ANONYMOUS',
};
/**
 * Slozeni urovne z nosicu. `source` rika, cim je uroven podlozena —
 * predplatne ma prednost (plati vsude), pak koupeny report, pak nic.
 */
export function mapAccessFor(carriers) {
    if (carriers.hasActiveSubscription) {
        return {
            baseLevel: MAP_SUBSCRIPTION_LEVEL,
            reportPoints: carriers.reportPoints,
            source: 'SUBSCRIPTION',
        };
    }
    if (carriers.reportPoints.length > 0) {
        return { baseLevel: 'Z', reportPoints: carriers.reportPoints, source: 'REPORT' };
    }
    return ANONYMOUS_MAP_ACCESS;
}
/**
 * Vzdalenost dvou bodu WGS84 v metrech (ekvidistantni priblizeni na
 * stredni sirce). Pro stovky metru v CR je chyba pod 0,1 % — presnejsi
 * geodezii drzi geo-api v PostGIS, tady jde o rozhodnuti „je to ta parcela,
 * za kterou clovek zaplatil".
 */
const EARTH_RADIUS_M = 6_371_000;
const D2R = Math.PI / 180;
export function mapAccessDistanceM(a, b) {
    const meanLat = ((a.lat + b.lat) / 2) * D2R;
    const dx = (b.lon - a.lon) * D2R * Math.cos(meanLat);
    const dy = (b.lat - a.lat) * D2R;
    return Math.sqrt(dx * dx + dy * dy) * EARTH_RADIUS_M;
}
/** `true`, kdyz bod lezi v okoli nektere zaplacene objednavky. */
export function isPointUnlockedByReport(access, point) {
    if (point === null)
        return false;
    return access.reportPoints.some((granted) => mapAccessDistanceM(granted, point) <= granted.radiusM);
}
/**
 * Uroven pro KONKRETNI MISTO. `null` = misto nezname (seznam vysledku,
 * hledac bez vyberu) — pak plati jen `baseLevel`, koupeny report se na nic
 * neuplatnuje.
 */
export function mapLevelAt(access, point) {
    return mapLevelWithReportUnlock(access, isPointUnlockedByReport(access, point));
}
/**
 * Totez jako `mapLevelAt`, jen pro volajiciho, ktery si odpoved „lezi to
 * v okoli zaplaceneho reportu?" zjistil JINAK NEZ POROVNANIM SOURADNIC.
 *
 * Krok P1MAP4b: geo-api se u parcely pta geo-db (`ST_DWithin` nad hranici
 * parcely v `ruian.parcely`), protoze parcela je PLOCHA — porovnani stredu
 * s bodem objednavky by u velkeho pozemku lhalo oba smery. Skladani urovne
 * ale zustava TADY, aby nebylo ve dvou kopiich (CLAUDE.md §8 pravidlo 2).
 */
export function mapLevelWithReportUnlock(access, unlockedByReport) {
    if (!unlockedByReport)
        return access.baseLevel;
    return hasMapEntitlement(access.baseLevel, MAP_REPORT_LEVEL)
        ? access.baseLevel
        : MAP_REPORT_LEVEL;
}
/** `true`, kdyz uroven v danem miste staci na funkci `feature`. */
export function canAccessMapFeatureAt(access, point, feature) {
    return hasMapEntitlement(mapLevelAt(access, point), MAP_FEATURE_ENTITLEMENTS[feature]);
}
//# sourceMappingURL=access.js.map